import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { log } from "../../lib/log";

// ---------------------------------------------------------------------------
// Sharing Tab — owner-only. Tracking-only metrics for account-sharing detection.
// No enforcement; just visibility so you can decide if/when to lock things down.
// ---------------------------------------------------------------------------
export function SharingTab() {
  const { apiFetch } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);  // { email, sessions }
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch(`/api/admin/sharing-stats?days=${days}`);
      setData(d);
    } catch (e) {
      log.warn("[admin] sharing-stats failed:", e?.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, days]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (email) => {
    setDetailLoading(true);
    setDetail({ email, sessions: [] });
    try {
      const d = await apiFetch(`/api/admin/sharing-stats/user/${encodeURIComponent(email)}`);
      setDetail(d);
    } catch (e) {
      setDetail({ email, sessions: [], error: e?.message });
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading || !data) {
    return <div className="mt-8 p-12 text-center text-sm text-[var(--srs-ink-dim)]">Loading…</div>;
  }

  const maxBucket = Math.max(1, ...data.histogram.map((b) => b.users));

  return (
    <div className="mt-8 space-y-8" data-testid="sharing-tab">
      <div>
        <div className="text-[10px] tracking-[0.3em] font-bold text-[var(--srs-ink-faint)] mb-2">// SHARING WATCH</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[var(--srs-ink)]">
          Account-sharing visibility
        </h2>
        <p className="mt-2 text-sm text-[var(--srs-ink-dim)] max-w-2xl">
          We track unique devices per user — purely for visibility. No enforcement, no auto-logout. Use this to decide if/when to add a device cap.
        </p>
        <div className="mt-4 flex items-center gap-3">
          <label className="text-[10px] tracking-[0.2em] font-bold text-[var(--srs-ink-faint)] uppercase">Window</label>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}
                  className="bg-[var(--srs-surface-2)] border border-[var(--srs-line)] text-[var(--srs-ink)] text-sm px-3 py-1.5 rounded outline-none focus:border-[var(--srs-sun)]"
                  data-testid="sharing-window-select">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
          <button onClick={load} className="text-[10px] tracking-[0.2em] font-bold uppercase px-3 py-2 border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] rounded">
            Refresh
          </button>
        </div>
      </div>

      {/* Top-line stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <StatBlock label="TOTAL SUBSCRIBERS" value={data.total_subscribers} accent="#22D3EE" />
        <StatBlock label="ACTIVE IN WINDOW" value={data.active_subscribers_in_window} accent="#10B981" />
        <StatBlock label="AVG DEVICES / USER" value={data.avg_devices_per_active_user} accent="#FF8A5C" />
        <StatBlock label="SUSPICIOUS (3+ DEVICES)" value={`${data.suspicious_count} · ${data.suspicious_share_pct}%`} accent="#EF4444" />
      </div>

      {/* Histogram */}
      <div className="border border-[var(--srs-line)] bg-[var(--srs-surface)] rounded-lg p-6">
        <div className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)] mb-4">DEVICES PER USER · DISTRIBUTION</div>
        {data.histogram.length === 0 ? (
          <div className="text-sm text-[var(--srs-ink-dim)] py-6 text-center">No device sessions in this window yet.</div>
        ) : (
          <div className="space-y-2">
            {data.histogram.map((b) => (
              <div key={b.devices} className="flex items-center gap-3">
                <div className="text-xs text-[var(--srs-ink-dim)] w-24 font-mono">{b.devices} device{b.devices === 1 ? "" : "s"}</div>
                <div className="flex-1 bg-[var(--srs-surface-2)] rounded-full h-6 overflow-hidden border border-[var(--srs-line-soft)]">
                  <div className="h-full transition-all duration-500"
                       style={{
                         width: `${(b.users / maxBucket) * 100}%`,
                         background: b.devices >= 3 ? "var(--srs-warn)" : "var(--srs-sun)",
                       }}>
                  </div>
                </div>
                <div className="text-sm text-[var(--srs-ink)] font-bold w-16 text-right">{b.users}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Suspicious accounts */}
      <div className="border border-[var(--srs-line)] bg-[var(--srs-surface)] rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--srs-line)] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">3+ DEVICES IN WINDOW</div>
            <div className="text-sm text-[var(--srs-ink-dim)] mt-1">Sorted by device count. Click to drill down.</div>
          </div>
        </div>
        {data.suspicious.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-[var(--srs-good)] text-2xl mb-2">✓</div>
            <div className="text-sm text-[var(--srs-ink-dim)]">No accounts with 3+ devices. Looks healthy.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--srs-surface-2)] border-b border-[var(--srs-line)]">
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">EMAIL</th>
                <th className="text-right px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">DEVICES</th>
                <th className="text-right px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">IPs</th>
                <th className="text-right px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">LOGINS</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">LAST SEEN</th>
                <th className="text-right px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]"> </th>
              </tr>
            </thead>
            <tbody data-testid="sharing-suspicious-table">
              {data.suspicious.map((row) => (
                <tr key={row.user_email} className="border-b border-[var(--srs-line-soft)] last:border-0">
                  <td className="px-4 py-3 font-mono text-xs text-[var(--srs-ink)]">{row.user_email}</td>
                  <td className="px-4 py-3 text-right font-bold text-[var(--srs-warn)]">{row.device_count}</td>
                  <td className="px-4 py-3 text-right text-[var(--srs-ink)]">{row.ip_count}</td>
                  <td className="px-4 py-3 text-right text-[var(--srs-ink-dim)]">{row.total_logins}</td>
                  <td className="px-4 py-3 text-xs text-[var(--srs-ink-dim)]">{row.last_seen ? new Date(row.last_seen).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openDetail(row.user_email)}
                            className="text-[10px] tracking-[0.2em] font-bold uppercase text-[var(--srs-sun)] hover:underline"
                            data-testid={`sharing-detail-${row.user_email}`}>
                      Inspect →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Drill-down modal */}
      {detail && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-[var(--srs-surface)] border border-[var(--srs-line)] rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-[var(--srs-line)] flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">DEVICE SESSIONS</div>
                <div className="text-base font-mono text-[var(--srs-ink)] mt-1">{detail.email}</div>
              </div>
              <button onClick={() => setDetail(null)} className="text-[var(--srs-ink-faint)] hover:text-[var(--srs-ink)]" data-testid="sharing-detail-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {detailLoading ? (
                <div className="p-12 text-center text-sm text-[var(--srs-ink-dim)]">Loading…</div>
              ) : (detail.sessions || []).length === 0 ? (
                <div className="p-12 text-center text-sm text-[var(--srs-ink-dim)]">No sessions recorded yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[var(--srs-surface)] border-b border-[var(--srs-line)]">
                    <tr>
                      <th className="text-left px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">DEVICE</th>
                      <th className="text-left px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">IP</th>
                      <th className="text-right px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">LOGINS</th>
                      <th className="text-left px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">FIRST SEEN</th>
                      <th className="text-left px-4 py-3 text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]">LAST SEEN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.sessions.map((s) => (
                      <tr key={s.device_id} className="border-b border-[var(--srs-line-soft)] last:border-0">
                        <td className="px-4 py-3 text-[var(--srs-ink)]">
                          <div className="font-medium">{s.user_agent_summary || "Unknown"}</div>
                          <div className="font-mono text-[10px] text-[var(--srs-ink-faint)] mt-0.5">{s.device_id}{s.derived ? " (derived)" : ""}</div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[var(--srs-ink-dim)]">{s.ip || "—"}</td>
                        <td className="px-4 py-3 text-right text-[var(--srs-ink)]">{s.login_count}</td>
                        <td className="px-4 py-3 text-xs text-[var(--srs-ink-dim)]">{s.first_seen ? new Date(s.first_seen).toLocaleString() : "—"}</td>
                        <td className="px-4 py-3 text-xs text-[var(--srs-ink-dim)]">{s.last_seen ? new Date(s.last_seen).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function StatBlock({ label, value, accent }) {
  return (
    <div className="border border-[var(--srs-line)] bg-[var(--srs-surface)] rounded-lg p-5">
      <div className="text-[10px] tracking-[0.25em] font-bold text-[var(--srs-ink-faint)]" style={{ color: accent }}>{label}</div>
      <div className="mt-2 text-3xl font-bold text-[var(--srs-ink)] tabular-nums">{value}</div>
    </div>
  );
}
